import React, { useEffect, useRef, useState } from 'react';
import { updateSessionNotes } from '../../api/sessionExtras';

/**
 * NotesTab - free-form interviewer notes with 10s autosave + `[mm:ss]` timestamp helper.
 *
 * Props:
 *   sessionId   - UUID
 *   initial     - initial notes string from the session record
 *   sessionStart- Date / ISO string used to compute elapsed time for the [mm:ss] tag
 */
export default function NotesTab({ sessionId, initial = '', sessionStart }) {
  const [value,   setValue]   = useState(initial || '');
  const [savedAt, setSavedAt] = useState(null);
  const [saving,  setSaving]  = useState(false);
  const taRef     = useRef(null);
  const lastSaved = useRef(initial || '');
  const startedAt = useRef(sessionStart ? new Date(sessionStart).getTime() : Date.now());

  useEffect(() => {
    if (sessionStart) startedAt.current = new Date(sessionStart).getTime();
  }, [sessionStart]);

  // 10s autosave loop
  useEffect(() => {
    if (!sessionId) return;
    const t = setInterval(async () => {
      if (value === lastSaved.current) return;
      setSaving(true);
      try {
        await updateSessionNotes(sessionId, value);
        lastSaved.current = value;
        setSavedAt(new Date());
      } catch (e) {
        console.warn('[NotesTab] autosave failed', e?.message);
      } finally {
        setSaving(false);
      }
    }, 10_000);
    return () => clearInterval(t);
  }, [sessionId, value]);

  // Best-effort flush on unmount
  useEffect(() => () => {
    if (sessionId && value !== lastSaved.current) {
      updateSessionNotes(sessionId, value).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const insertTimestamp = () => {
    const elapsed = Math.max(0, Math.floor((Date.now() - startedAt.current) / 1000));
    const mm = String(Math.floor(elapsed / 60)).padStart(2, '0');
    const ss = String(elapsed % 60).padStart(2, '0');
    const tag = `[${mm}:${ss}] `;

    const ta = taRef.current;
    const start = ta?.selectionStart ?? value.length;
    const end   = ta?.selectionEnd   ?? value.length;
    const next = value.slice(0, start) + tag + value.slice(end);
    setValue(next);
    requestAnimationFrame(() => {
      if (taRef.current) {
        const pos = start + tag.length;
        taRef.current.focus();
        taRef.current.setSelectionRange(pos, pos);
      }
    });
  };

  return (
    <div className="flex flex-col h-full p-3 gap-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={insertTimestamp}
          className="px-2 py-1 text-[10px] font-semibold rounded bg-slate-800
                     hover:bg-slate-700 text-slate-200 border border-slate-700"
        >
          + [mm:ss]
        </button>
        <span className="ml-auto text-[10px] text-slate-500">
          {saving ? 'Saving…' : savedAt ? `Saved ${savedAt.toLocaleTimeString()}` : 'Autosaves every 10s'}
        </span>
      </div>
      <textarea
        ref={taRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Live notes - autosaves every 10 seconds. Use the [mm:ss] button to mark moments."
        className="flex-1 w-full resize-none rounded-lg bg-slate-900 border border-slate-800
                   text-slate-100 text-sm p-3 focus:outline-none focus:ring-1
                   focus:ring-blue-500 font-mono leading-relaxed"
      />
    </div>
  );
}
